/**
 * Admin modules: clinics, clinicAccess, users, doctors, staff,
 * audit, errorLogs, analytics, security, inventory, profile, ai,
 * reports, schedule, schedules, doctorStatus.
 */
import { fetchApi, fetchMultipartApi, fetchGetFileApi, API_BASE_URL } from '../client';
import { csrfHeader } from '../../csrf';
import { buildQS } from '../helpers';
import type {
  Clinic, Doctor, StaffMember, AuditLog, Profile,
  ProfileUpdateRequest, UploadAvatarResponse,
  ScheduleSlot, DoctorStatusEvent, CreateDoctorStatusEventPayload,
  InventoryItem, InventoryConfig, InventoryCreateRequest, InventoryUpdateRequest,
  StaffInviteRequest, StaffStatusUpdateRequest, AuditLogsResponse,
  AnalyticsDashboardResponse, DoctorDashboardResponse, ClinicAdminDashboardResponse,
  DoctorStats,
  DoctorClinic, SystemErrorLog,
} from '../../../types';
import type { PagedResponse } from '../../../types/pagination';
import type { UserSecuritySettings, SetupPinRequest, VerifyPinRequest, PinVerificationResponse, SecuritySettingsRequest } from '../../../types/security';
import { hashPasswordForTransmission, PASSWORD_HASH_HEADER, PASSWORD_HASH_VALUE } from '../../loginSecurity';

/**
 * Password step-up for sensitive self-service changes (medopd-docs 96): the account password is sent
 * the way login sends it — PBKDF2 salted with the email plus the X-Password-Format header, or plaintext
 * when WebCrypto is unavailable (the backend then hashes it itself).
 */
async function passwordForTransport(
  password: string,
  email: string,
): Promise<{ value: string; headers?: Record<string, string> }> {
  try {
    const value = await hashPasswordForTransmission(password, email.trim().toLowerCase());
    return { value, headers: { [PASSWORD_HASH_HEADER]: PASSWORD_HASH_VALUE } };
  } catch {
    return { value: password };
  }
}

/** A profile update that may also change the sign-in email (medopd-docs 96). */
export interface ProfileEmailChange {
  /** The email the account signs in with today. */
  currentEmail: string;
  /** The account password, typed by the user. */
  currentPassword: string;
}

export const clinicsModule = {
  getAll: () => fetchApi<Clinic[]>('/api/clinics'),
  get: (id: string) => fetchApi<Clinic>(`/api/clinics/${id}`),
  create: (data: Partial<Clinic>) =>
    fetchApi<Clinic>('/api/clinics', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Clinic>) =>
    fetchApi<Clinic>(`/api/clinics/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  uploadLogo: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchMultipartApi<{ logoUrl: string }>(`/api/clinics/${id}/logo`, formData);
  },
  deleteLogo: (id: string) =>
    fetchApi<{ success: boolean }>(`/api/clinics/${id}/logo`, { method: 'DELETE' }),

  createWithAdmin: async (data: {
    clinic: Partial<Clinic> & {
      subscriptionPlanId: string;
      billingCycle?: 'MONTHLY' | 'ANNUAL';
      maxDoctorsOverride?: number;
      maxStaffOverride?: number;
    };
    admin: { name: string; email: string; mobile: string };
  }) => {
    const res = await fetchApi<{ clinic: Clinic; tempPassword: string }>('/api/clinics/with-admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  // includeTestData surfaces Test Data Studio tenants (hidden by default) so the Access Management
  // screen can enable modules (AI, etc.) on a generated test clinic.
  getClinics: async (includeTestData = false) => {
    const res = await fetchApi<Clinic[]>(`/api/clinics${includeTestData ? '?includeTestData=true' : ''}`);
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },

  /** Super-admin clinic list, paged + searched + status-filtered on the server (medopd-docs 112). */
  getClinicsPaged: async (params: { page: number; size: number; search?: string; status?: string }) => {
    const res = await fetchApi<PagedResponse<Clinic>>(`/api/clinics${buildQS(params)}`);
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  /** Headline counts for the clinic list (the list is paged, so they can't come from its rows). */
  getClinicStats: async () => {
    const res = await fetchApi<{ total: number; setupCompleted: number; pendingSetup: number }>('/api/clinics/stats');
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  /** Lightweight id + name list for clinic pickers (no invitation lookups, no full DTOs). */
  getClinicOptions: async () => {
    const res = await fetchApi<Array<{ id: string; name: string; status?: string }>>('/api/clinics/options');
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },

  resendInvitation: async (clinicId: string) => {
    const res = await fetchApi<{ message: string; expiresAt: string }>(
      `/api/clinics/${clinicId}/resend-invitation`,
      { method: 'POST' }
    );
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  deactivate: async (clinicId: string) => {
    const res = await fetchApi<{ message: string }>(`/api/clinics/${clinicId}/deactivate`, { method: 'POST' });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },

  activate: async (clinicId: string) => {
    const res = await fetchApi<{ message: string }>(`/api/clinics/${clinicId}/activate`, { method: 'POST' });
    if (res.error) throw new Error(res.error);
    return res.data!;
  },
};

export const clinicAccessModule = {
  getModules: (clinicId: string) =>
    fetchApi<Array<{ code: string; name: string; description: string; enabled: boolean }>>(
      `/api/admin/clinics/${clinicId}/modules`
    ),

  setModule: (clinicId: string, moduleCode: string, enabled: boolean) =>
    fetchApi<{ message: string }>(`/api/admin/clinics/${clinicId}/modules/${moduleCode}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  getRoleMatrix: (clinicId: string) =>
    fetchApi<Record<string, Array<{
      code: string; name: string; moduleCode: string;
      defaultGranted: boolean; overrideGranted: boolean | null; effectiveGranted: boolean;
    }>>>(`/api/admin/clinics/${clinicId}/role-permissions`),

  setRolePermission: (clinicId: string, roleCode: string, permissionCode: string, granted: boolean) =>
    fetchApi<{ message: string }>(
      `/api/admin/clinics/${clinicId}/role-permissions/${roleCode}/${permissionCode}`,
      { method: 'PUT', body: JSON.stringify({ granted }) }
    ),

  removeRolePermission: (clinicId: string, roleCode: string, permissionCode: string) =>
    fetchApi<{ message: string }>(
      `/api/admin/clinics/${clinicId}/role-permissions/${roleCode}/${permissionCode}`,
      { method: 'DELETE' }
    ),
};

export const doctorsModule = {
  getAll: () => fetchApi<Doctor[]>('/api/doctors'),
  create: (data: Partial<Doctor>) =>
    fetchApi<Doctor>('/api/doctors', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Doctor>) =>
    fetchApi<Doctor>(`/api/doctors/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivate: (id: string) =>
    fetchApi<Doctor>(`/api/doctors/${id}/deactivate`, { method: 'PATCH' }),
  getStats: (doctorId: string, period: 'today' | 'week' | 'month' = 'today') =>
    fetchApi<DoctorStats>(`/api/doctors/${doctorId}/stats?period=${period}`),
  // Multi-clinic support
  listClinics: (userId: string) =>
    fetchApi<DoctorClinic[]>(`/api/v1/doctors/${userId}/clinics`),
  availableClinics: (userId: string) =>
    fetchApi<{ clinicId: string; clinicName: string }[]>(`/api/v1/doctors/${userId}/clinics/available`),
  assignToClinic: (userId: string, clinicId: string) =>
    fetchApi<{ doctorProfileId: string; userId: string; clinicId: string; message: string }>(
      `/api/v1/doctors/${userId}/clinics`,
      { method: 'POST', body: JSON.stringify({ clinicId }) }
    ),
  removeFromClinic: (userId: string, clinicId: string) =>
    fetchApi<{ message: string }>(
      `/api/v1/doctors/${userId}/clinics/${clinicId}`,
      { method: 'DELETE' }
    ),
};

export const staffModule = {
  getAll: () => fetchApi<StaffMember[]>('/api/staff'),
  invite: (data: StaffInviteRequest) =>
    fetchApi<StaffMember>('/api/staff/invite', { method: 'POST', body: JSON.stringify(data) }),
  getInvitations: () => fetchApi('/api/staff/invitations'),
  resendInvitation: (invitationId: string) =>
    fetchApi(`/api/staff/invitations/${invitationId}/resend`, { method: 'POST' }),
  updateStatus: (id: string, isActive: boolean) =>
    fetchApi<StaffMember>(`/api/staff/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive } as StaffStatusUpdateRequest),
    }),
  updateRole: (id: string, role: string) =>
    fetchApi<StaffMember>(`/api/staff/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
};

export const auditModule = {
  getLogs: (limit = 50, clinicId?: string, page = 0) =>
    fetchApi<AuditLog[] | AuditLogsResponse>(
      `/api/audit/logs${buildQS({ limit, clinicId, page })}`
    ),
  /**
   * Slice-style page of audit logs (medopd-docs 112): `hasNext`, never a total — counting audit_logs on
   * every page turn is what medopd-docs 107 removed. Rows are the raw audit_logs entity.
   */
  getLogsSlice: (page: number, size: number, clinicId?: string) =>
    fetchApi<AuditLogSlice>(`/api/audit/logs${buildQS({ page, size, clinicId })}`),
};

/** One audit_logs row as the backend serialises it. */
export interface AuditLogEntry {
  id: number | string;
  actorId?: string | null;
  actorRole?: string | null;
  action: string;
  resourceType?: string | null;
  resourceId?: string | null;
  patientId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  changeSummary?: string | null;
  status?: string | null;
  errorMessage?: string | null;
  createdAt: string;
}

export interface AuditLogSlice {
  content: AuditLogEntry[];
  page: number;
  size: number;
  hasNext: boolean;
  last: boolean;
}

export const errorLogsModule = {
  getLogs: (options?: {
    limit?: number; clinicId?: string; startDate?: string; endDate?: string;
    level?: string; statusCode?: number; search?: string;
  }) => {
    const params = new URLSearchParams();
    params.append('limit', String(options?.limit ?? 100));
    if (options?.clinicId) params.append('clinicId', options.clinicId);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.level) params.append('level', options.level);
    if (options?.statusCode) params.append('statusCode', String(options.statusCode));
    if (options?.search) params.append('search', options.search);
    return fetchApi<any[]>(`/api/admin/error-logs?${params.toString()}`);
  },
  /** Paged error logs with every filter applied server-side (medopd-docs 112). */
  getLogsPaged: (options: {
    page: number; size: number; clinicId?: string; startDate?: string; endDate?: string;
    level?: string; statusCode?: number; search?: string;
  }) => fetchApi<PagedResponse<SystemErrorLog>>(`/api/admin/error-logs${buildQS(options)}`),
  getById: (id: number) => fetchApi<any>(`/api/admin/error-logs/${id}`),
};

export const analyticsModule = {
  getDashboard: (range?: number) =>
    fetchApi<AnalyticsDashboardResponse>(
      `/api/analytics/dashboard${range ? `?range=${range}` : ''}`
    ),
  getDoctorDashboard: (range = 30) =>
    fetchApi<DoctorDashboardResponse>(`/api/analytics/doctor-dashboard?range=${range}`),
  getClinicAdminDashboard: (range = 30) =>
    fetchApi<ClinicAdminDashboardResponse>(`/api/analytics/clinic-admin-dashboard?range=${range}`),
};

export const securityModule = {
  getSettings: () =>
    fetchApi<UserSecuritySettings>('/api/user-security/settings'),
  setupPin: (data: SetupPinRequest) =>
    fetchApi<UserSecuritySettings>('/api/user-security/setup-pin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  verifyPin: (data: VerifyPinRequest) =>
    fetchApi<PinVerificationResponse>('/api/user-security/verify-pin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateSettings: (data: SecuritySettingsRequest) =>
    fetchApi<UserSecuritySettings>('/api/user-security/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  /** Forgot PIN: clears the PIN and the lock, after re-checking the account password (medopd-docs 96). */
  resetPin: async (password: string, email: string) => {
    const { value, headers } = await passwordForTransport(password, email);
    return fetchApi<void>('/api/user-security/reset-pin', {
      method: 'POST',
      skipBodyLog: true,
      body: JSON.stringify({ password: value }),
      headers,
    });
  },
  /** Admin only: clears ANOTHER user's PIN lockout. There is no self-service reset (medopd-docs 96). */
  resetFailedAttempts: (targetUserId: string) =>
    fetchApi<void>(
      `/api/user-security/reset-failed-attempts?targetUserId=${encodeURIComponent(targetUserId)}`,
      { method: 'POST' }
    ),
};

export const inventoryModule = {
  getAll: () => fetchApi<InventoryItem[]>('/api/admin/inventory'),
  /** One page of the clinic's items by name, searched on name/SKU (medopd-docs 117). */
  getPage: (page: number, size: number, search?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search?.trim()) params.set('search', search.trim());
    return fetchApi<PagedResponse<InventoryItem>>(`/api/admin/inventory?${params.toString()}`);
  },
  getConfig: () => fetchApi<InventoryConfig>('/api/admin/inventory/config'),
  updateConfig: (config: InventoryConfig) =>
    fetchApi<InventoryConfig>('/api/admin/inventory/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  create: (item: InventoryCreateRequest) =>
    fetchApi<InventoryItem>('/api/admin/inventory', {
      method: 'POST',
      body: JSON.stringify(item),
    }),
  update: (id: string, item: InventoryUpdateRequest) =>
    fetchApi<InventoryItem>(`/api/admin/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(item),
    }),
  /** Removes the item from the clinic's list for good. */
  remove: (id: string) =>
    fetchApi<null>(`/api/admin/inventory/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** How many items are running low and how many are out of stock. */
  getSummary: () =>
    fetchApi<{ lowStock: number; outOfStock: number }>('/api/admin/inventory/summary/status'),
};

export const profileModule = {
  getMyProfile: () => fetchApi<Profile>('/api/profile/me'),
  /**
   * Saves the profile. Changing the sign-in email needs `emailChange` (medopd-docs 96): the password is
   * sent hashed under the CURRENT email (to verify it) and under the NEW email (so the stored hash can
   * be re-salted). On success the backend ends every session and answers `signedOut: true`.
   */
  updateMyProfile: async (data: ProfileUpdateRequest, emailChange?: ProfileEmailChange) => {
    if (!emailChange || !data.email) {
      return fetchApi<Profile & { signedOut?: boolean }>('/api/profile/me', { method: 'PUT', body: JSON.stringify(data) });
    }
    const current = await passwordForTransport(emailChange.currentPassword, emailChange.currentEmail);
    const body: Record<string, unknown> = { ...data, currentPassword: current.value };
    if (current.headers) {
      body.newEmailPassword = (await passwordForTransport(emailChange.currentPassword, data.email)).value;
    }
    return fetchApi<Profile & { signedOut?: boolean }>('/api/profile/me', {
      method: 'PUT',
      skipBodyLog: true,
      body: JSON.stringify(body),
      headers: current.headers,
    });
  },
  uploadAvatar: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_BASE_URL}/api/profile/avatar`, {
      method: 'POST',
      headers: { ...csrfHeader('POST') }, // cookie auth (credentials below) + CSRF token
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to upload avatar');
    return response.json() as Promise<UploadAvatarResponse>;
  },
};

export const aiModule = {
  getPatientSummary: (patientId: string) =>
    fetchApi<string>(`/api/ai/patient-summary/${patientId}`),
  getLabTrends: (patientId: string) =>
    fetchApi<any>(`/api/ai/lab-trends/${patientId}`),
  checkPrescriptionSafety: (patientId: string, prescriptionData: any) =>
    fetchApi<any>(`/api/ai/prescription-safety-check/${patientId}`, {
      method: 'POST',
      body: JSON.stringify(prescriptionData),
    }),
  polishNotes: (notes: string, type: 'chief_complaint' | 'clinical_note') =>
    fetchApi<{ original: string; polished: string; keptOriginal?: boolean; missingTerms?: string[] }>('/api/ai/polish-notes', {
      method: 'POST',
      body: JSON.stringify({ notes, type }),
    }),
  analyzeLabReport: (filePath: string, patientId: string) =>
    fetchApi<any>('/api/ai/analyze-lab', {
      method: 'POST',
      body: JSON.stringify({ filePath, patientId }),
    }),
};

/** Daily / date-range report (medopd-docs 136 §1 X6): money from bill_payments, the Day Close ledger. */
export interface DailyReportResponse {
  from: string;
  to: string;
  total_visits: number;
  new_patients: number;
  followup_patients: number;
  total_revenue: number;
  cash_amount: number;
  card_amount: number;
  upi_amount: number;
  other_amount: number;
  collections_count: number;
  voided_bill_amount: number;
  visits: Array<{ id: string; doctorId: string | null; doctor_name: string; patient_name: string; is_new_patient: boolean; amount: number; payment_mode: string | null; timestamp: string }>;
  doctor_breakdown: Array<{ doctorId: string | null; doctor_name: string; patientCount: number; revenue: number }>;
}

export const reportsModule = {
  /** One day (from === to) or an inclusive range of at most 366 days. */
  getDailyReport: (from: string, to: string = from) =>
    fetchApi<DailyReportResponse>(`/api/reports/daily${buildQS({ from, to })}`),
  /** The same report as a CSV file. */
  exportDailyReportCsv: (from: string, to: string = from) =>
    fetchGetFileApi(`/api/reports/daily/export.csv${buildQS({ from, to })}`),
};

export const scheduleModule = {
  getSchedule: (doctorId?: string, date?: string) =>
    fetchApi<ScheduleSlot[]>(`/api/doctor/schedule${buildQS({ doctorId, date })}`),
  createSlot: (data: { doctorId: string; date: string; startTime: string; endTime: string }) =>
    fetchApi<ScheduleSlot>('/api/doctor/schedule', { method: 'POST', body: JSON.stringify(data) }),
  blockSlot: (slotId: string, reason = 'Staff request') =>
    fetchApi<ScheduleSlot>(`/api/doctor/schedule/${slotId}/block`, {
      method: 'PATCH',
      body: JSON.stringify({ reason }),
    }),
  unblockSlot: (slotId: string) =>
    fetchApi<ScheduleSlot>(`/api/doctor/schedule/${slotId}/unblock`, { method: 'PATCH' }),
};

export const schedulesModule = {
  getConfig: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config`),
  saveConfig: (doctorId: string, config: any) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config`, {
      method: 'POST',
      body: JSON.stringify(config),
    }),
  activate: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config/activate`, { method: 'PATCH' }),
  deactivate: (doctorId: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/schedule-config/deactivate`, { method: 'PATCH' }),
  getSlots: (doctorId: string, options?: { startDate?: string; endDate?: string }) => {
    if (!options?.startDate || !options?.endDate) {
      const today = new Date();
      const end = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      return fetchApi<any>(
        `/api/v1/doctors/${doctorId}/slots/range?startDate=${today.toISOString().split('T')[0]}&endDate=${end.toISOString().split('T')[0]}`
      );
    }
    return fetchApi<any>(
      `/api/v1/doctors/${doctorId}/slots/range?startDate=${options.startDate}&endDate=${options.endDate}`
    );
  },
  getSlotsByDate: (doctorId: string, date: string) =>
    fetchApi<any>(`/api/v1/doctors/${doctorId}/slots?date=${date}`),
  getAppointments: (doctorId: string, date?: string) => {
    const d = date || new Date().toISOString().split('T')[0];
    return fetchApi<any>(`/api/appointments?doctorId=${doctorId}&date=${d}`);
  },
  /** All appointments in [startDate, endDate]. For a doctor caller the backend forces their own id. */
  getAppointmentsRange: (doctorId: string, startDate: string, endDate: string) =>
    fetchApi<any>(`/api/appointments/range?${new URLSearchParams({ doctorId, startDate, endDate }).toString()}`),

  // Slot-based booking (AppointmentController, /api/v1/appointments). Clinic comes from the tenant
  // context server-side.
  bookAppointment: (data: {
    doctorId: string;
    visitDate: string;
    slotId: string;
    /** The chosen time ("HH:mm"), so the server books exactly what was shown. */
    startTime?: string;
    endTime?: string;
    visitType: 'NEW' | 'FOLLOWUP';
    patientId: string;
    patientName: string;
    mobile?: string;
  }) =>
    fetchApi<any>('/api/v1/appointments', { method: 'POST', body: JSON.stringify(data) }),
  rescheduleAppointment: (appointmentId: string, newSlotId: string, reason?: string) =>
    fetchApi<any>(`/api/v1/appointments/${encodeURIComponent(appointmentId)}/reschedule`, {
      method: 'PUT',
      body: JSON.stringify({ appointmentId, newSlotId, reason }),
    }),
  cancelAppointment: (appointmentId: string) =>
    fetchApi<any>(`/api/v1/appointments/${encodeURIComponent(appointmentId)}`, { method: 'DELETE' }),
  /** Blocks one time in the list (recorded as doctor time-off). doctorId is needed for times from the generated list. */
  blockSlot: (slotId: string, reason?: string, doctorId?: string) =>
    fetchApi<any>(`/api/v1/appointment-slots/${encodeURIComponent(slotId)}/block`, {
      method: 'POST',
      body: JSON.stringify({ slotId, reason, doctorId }),
    }),
  unblockSlot: (slotId: string, doctorId?: string) =>
    fetchApi<any>(`/api/v1/appointment-slots/${encodeURIComponent(slotId)}/unblock${doctorId ? `?doctorId=${encodeURIComponent(doctorId)}` : ''}`, { method: 'PATCH' }),
};

export const doctorStatusModule = {
  list: (doctorId?: string, start?: string, end?: string) =>
    fetchApi<DoctorStatusEvent[]>(`/api/doctor/status-events${buildQS({ doctorId, start, end })}`),
  current: (doctorId?: string) =>
    fetchApi<DoctorStatusEvent>(
      `/api/doctor/status-events/current${doctorId ? `?doctorId=${doctorId}` : ''}`
    ),
  create: (payload: CreateDoctorStatusEventPayload) =>
    fetchApi<DoctorStatusEvent>('/api/doctor/status-events', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};
